import React, { useState, useEffect } from 'react';

import {
  Box,
  List,
  Button,
  Tooltip,
  ListItem,
  IconButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useMountedState } from 'react-use';
import { isEmpty, isFunction } from 'lodash';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import BackupIcon from '@mui/icons-material/Backup';
import FileCopyIcon from '@mui/icons-material/FileCopy';

const Files = ({
  accept,
  getFiles,
  children,
  onSetFiles,
  autoUpload = false,
  onUpload = () => {},
  onDelete = () => {},
}) => {
  const { t } = useTranslation();
  const isMounted = useMountedState();

  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpload = async (newFiles) => {
    setIsSubmitting(true);
    await onUpload(newFiles || files);
    if (isMounted) {
      setIsSubmitting(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    setFiles(acceptedFiles);
    if (autoUpload) {
      await handleUpload(acceptedFiles);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept,
  });

  useEffect(() => {
    isFunction(onSetFiles) && onSetFiles(files);
  }, [files, onSetFiles]);

  useEffect(() => {
    !isEmpty(getFiles) && setFiles(getFiles);
  }, [getFiles]);

  return (
    <div style={isSubmitting ? { pointerEvents: 'none', opacity: 0.3 } : {}}>
      <Box bgcolor="lightgrey" p="20px" textAlign="center" {...getRootProps()}>
        <input {...getInputProps()} />
        <BackupIcon />
        <Box>{t('drop files here')}</Box>
      </Box>

      <List>
        {!isFunction(onSetFiles) &&
          files.map((file) => (
            <ListItem divider key={file.name}>
              <ListItemIcon>
                <FileCopyIcon />
              </ListItemIcon>
              <ListItemText
                primary={file.name}
                primaryTypographyProps={{ variant: 'h5' }}
              />
              <Tooltip title={t('discard')}>
                <IconButton
                  edge="end"
                  onClick={() => {
                    const newFiles = files.filter((f) => f.name !== file.name);
                    setFiles(newFiles);
                    onDelete(newFiles);
                  }}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </ListItem>
          ))}
      </List>
      {children}
      {files.length > 0 && !autoUpload && (
        <Button variant="outlined" onClick={() => handleUpload()}>
          {t('upload')}
        </Button>
      )}
    </div>
  );
};

Files.propTypes = {
  onUpload: PropTypes.func,
  onDelete: PropTypes.func,
  children: PropTypes.node,
  getFiles: PropTypes.array,
  onSetFiles: PropTypes.func,
  autoUpload: PropTypes.bool,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default Files;
